<template>
    <div class="custom-input-text flex align-items-center gap-2 relative" :style="'width: ' + width + 'px;'">
        <CustomLabel :name="name" :required="required" :label="label"/>
        <InputText :id="name" type="text" v-model="internalValue" :disabled="disabled"
                   :placeholder="computedPlaceholder" :maxlength="maxLength"
                   :class="computedClasses" @input="handleChange" @blur="handleBlur" :rules="rules"
                   :dir="dir" @keydown="onKeydown" @paste="onPaste"/>
        <CustomValidationMessage :errorMessage="errorMessage" />
        <CustomValidationMessage :errorMessage="customErrorMessage" />
    </div>
</template>

<script>
    import InputText from "primevue/inputtext";
    import Tooltip from "primevue/tooltip";
    import {useField} from "vee-validate";
    import CustomLabel from "./inner/CustomLabel";
    import CustomValidationMessage from "./inner/CustomValidationMessage";
    import {watch} from 'vue';

    export default {
        name: "CustomInputText",

        props: {
            name: {
                type: String,
            },
            label: {
                type: String,
            },
            width: {
                type: String,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            required: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: "",
            },
            noPlaceholder: {
                type: Boolean,
                default: false,
            },
            rules: undefined,
            modelValue: null,
            customErrorMessage: {
                type: String,
                default: "",
            },
            maxLength: {
                type: Number,
                default: null,
            },
            dir: {
                type: String,
                default: "",
            },
            disableBrackets: {
                type: Boolean,
                default: false,
            },
            forceUppercase: {
                type: Boolean,
                default: false,
            }
        },

        emits: ["update:modelValue"],

        directives: {
            tooltip: Tooltip,
        },

        methods: {
            onKeydown(event) {
                if (this.disableBrackets && ['(', ')'].includes(event.key)) {
                    event.preventDefault();
                }
            },

            onPaste(event) {
                if (!this.disableBrackets) return;

                const pasted = (event.clipboardData || window.clipboardData).getData('text');
                if (/[()]/.test(pasted)) {
                    event.preventDefault();
                    const clean = pasted.replace(/[()]/g, '');
                    document.execCommand('insertText', false, clean);
                }
            },
        },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    value = value && this.forceUppercase ? value.toUpperCase() : value;
                    this.$emit("update:modelValue", value);
                },
            },

            computedPlaceholder() {
                if (this.noPlaceholder) return "";
                if (this.placeholder === "") return this.$t('message.inputs.inputTextPlaceholder');
                else return this.placeholder;
            },

            computedClasses() {
                return this.errorMessage ? "p-invalid" : "";
            },
        },

        setup(props) {
            const {
                value: inputValue,
                errorMessage,
                handleBlur,
                handleChange,
                meta,
                setValue
            } = useField(props.name, props.rules, {
                initialValue: props.modelValue,
                validateOnInput: false,
                validateOnChange: false,
            });

            watch(
                () => props.modelValue,
                (newValue) => {
                    setValue(newValue);
                }
            );

            return {
                handleChange,
                handleBlur,
                errorMessage,
                inputValue,
                meta,
            };
        },

        components: {InputText, CustomLabel, CustomValidationMessage},
    };
</script>

<style scoped lang="scss">
</style>
